import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { flowRight } from 'lodash'

import { NavLink as ReactRouterLink, withRouter } from 'react-router-dom'
import { Context } from './AppContext'
import { NETTIX_VERTICAL_DOMAINS } from 'lib/constants'
import { logException } from 'lib/errors'
import { stringToLocationObject } from 'lib/location'
import { appendUtmTagQueryParamsToUrl, createUtmTagQueryParams } from 'lib/utm'

const Link = ({
    location,
    children,
    className,
    disabled,
    active,
    title,
    alt,
    to,
    exact,
    state,
    target,
    external,
    onClick,
    trackName,
    trackLabel,
    isActive,
    download,
    noEmptyWarning,
    utmTag,
    dataGtmName,
    dataGtmPage,
    dataGtmLocation,
}) => {
    to = to && to.trim ? to.trim() : to

    if (!to) {
        if (!noEmptyWarning) {
            logException(`Invalid Link: "to" is undefined or empty string. trackName: "${trackName}" trackLabel: "${trackLabel}"`)
        }
        return children
    }

    // default links with http protocol to be handled as external
    external = typeof external === 'undefined' ? (
        to.indexOf('http') === 0 ||
        to.indexOf('tel:') === 0 ||
        to.indexOf('mailto:') === 0
    ) : external

    let onClickSsoRefresh
    if (external) {
        if (utmTag) {
            const url = appendUtmTagQueryParamsToUrl(
                new URL(to),
                createUtmTagQueryParams(to, utmTag.campaign, utmTag.content)
            )
            to = url.href
        }
        // in case the link takes us to any nettix vertical, we want to do SSO refresh just in case session expired
        const domainUrl = to.replace('http://', '').replace('https://', '').replace('www.', '').replace('www-test.', '').split(/[/?#]/)[0]

        /**
         * There is one case where automatic login will not happen for already opened nettix site. That's why we need a forceful sso/refresh while doing navigation to other nettix sites. For Example,
         *
         * TAB1: Logged in to OmaNettix
         * TAB2: Logged out in Nettiauto
         * TAB2: Refresh Nettiauto page, Login will not happen
         * TAB1: Navigate to Nettiauto, Login will happen thru sso refresh call. (forceful sso/refresh)
         */
        if (NETTIX_VERTICAL_DOMAINS.includes(domainUrl)) {
            onClickSsoRefresh = (e) => {
                e.preventDefault()

                const arr = to.split('/')
                const ssoDomainUrl = arr[0] + '//' + arr[2]

                window.open(ssoDomainUrl + '/sso/refresh?successUrl=' + encodeURIComponent(to) + '&failureUrl=' + encodeURIComponent(to), target)
            }
        }
    }

    // default external links to target _blank
    target = !target && external ? '_blank' : target

    const commonProps = {
        title,
        alt,
        className: classNames(
            className,
            { disabled: disabled },
            { active: active },
        ),
        onClick: (e) => {
            if (disabled) e.preventDefault()
            if (onClickSsoRefresh) onClickSsoRefresh(e)
            if (onClick) onClick(e)
        },
    }

    if (state && external) {
        logException('State is not supported for external links: ', state)
    }

    const getComponentProps = () => {
        if (external) {
            return {
                ...commonProps,
                href: to,
                target: target,
                rel: target === '_blank' ? 'noopener noreferrer' : '',
                download,
            }
        } else {
            const urlParts = stringToLocationObject(to)

            return {
                ...commonProps,
                to: {
                    pathname: urlParts.pathname,
                    search: urlParts.search,
                    state: {
                        ...(location.state || {}),
                        ...(state || {})
                    },
                    hash: urlParts.hash,
                },
                target: target,
                exact,
                isActive,
            }
        }
    }

    return (
        <Context.Consumer>
            {context => {
                const contextName = context.join('.')
                return external ? (
                    <a
                        data-track-context={contextName}
                        data-track-name={trackName}
                        data-track-label={trackLabel}
                        data-gtm-name={dataGtmName}
                        data-gtm-page={dataGtmPage}
                        data-gtm-location={dataGtmLocation}
                        {...getComponentProps()}
                    >
                        {children}
                    </a>
                ) : (
                    <ReactRouterLink
                        data-track-context={contextName}
                        data-track-name={trackName}
                        data-track-label={trackLabel}
                        data-gtm-name={dataGtmName}
                        data-gtm-page={dataGtmPage}
                        data-gtm-location={dataGtmLocation}
                        {...getComponentProps()}
                    >
                        {children}
                    </ReactRouterLink>
                )
            }}
        </Context.Consumer>
    )
}

Link.propTypes = {
    to: (props, propName, componentName) => {
        return !props.noEmptyWarning && !props.to
            ? new Error(`'Failed prop type: The prop \`${propName}\` is marked as required in \`${componentName}\`, but its value is \`${props.to}\`.'`)
            : null
    },
    trackName: PropTypes.string.isRequired,
    trackLabel: PropTypes.string,
    className: PropTypes.string,
    noEmptyWarning: PropTypes.bool,
    utmTag: PropTypes.shape({
        campaign: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
    }),
    dataGtmName: PropTypes.string,
    dataGtmPage: PropTypes.string,
    location: PropTypes.object,
}

export default flowRight(
    withRouter,
)(Link)
